import { EntityDrawerView } from '@/components/EntityDrawerView';
import { Icon } from '@/components/ui/Icon';
import { AppVersion, AppVersionFormData } from '@/pages/app-versions/types';
import { mdiAndroid, mdiApple } from '@mdi/js';
import * as React from 'react';
import { AppVersionForm } from './AppVersionForm';

export interface AppVersionDrawerViewProps {
  isOpen: boolean;
  appVersion: AppVersion | null;
  mode: 'view' | 'create' | 'edit';
  isLoading: boolean;
  isFormLoading: boolean;
  onClose: () => void;
  onEdit: (appVersion: AppVersion) => void;
  onDelete: (appVersion: AppVersion) => void;
  onFormSubmit: (data: AppVersionFormData) => Promise<void>;
}

export const AppVersionDrawerView: React.FC<AppVersionDrawerViewProps> = ({
  isOpen,
  appVersion,
  mode,
  isLoading,
  isFormLoading,
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
}) => (
  <EntityDrawerView<AppVersion, AppVersionFormData>
    isOpen={isOpen}
    entity={appVersion}
    mode={mode}
    isLoading={isLoading}
    isFormLoading={isFormLoading}
    entityLabel='Version'
    spinnerColor='border-blue-600'
    onClose={onClose}
    onEdit={onEdit}
    onDelete={onDelete}
    onFormSubmit={onFormSubmit}
    getTitle={(entity, m) => {
      const platformLabel = entity
        ? entity.platform === 'ios'
          ? 'iOS'
          : 'Android'
        : '';
      switch (m) {
        case 'create':
          return 'Create New App Version';
        case 'edit':
          return entity ? `Edit ${platformLabel} Version` : 'Edit Version';
        default:
          return entity ? `${platformLabel} Version` : 'Version Details';
      }
    }}
    renderHeader={entity => {
      const isIos = entity.platform === 'ios';
      return (
        <div className='flex items-center space-x-4 pb-6 border-b border-grey-200'>
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isIos ? 'bg-grey-100' : 'bg-green-100'
            }`}
          >
            <Icon
              path={isIos ? mdiApple : mdiAndroid}
              size={40}
              className={isIos ? 'text-grey-800' : 'text-green-600'}
            />
          </div>
          <div className='flex-1'>
            <h3 className='text-xl font-semibold text-grey-900'>
              {isIos ? 'iOS' : 'Android'} App Version
            </h3>
            <div className='mt-2 flex items-center space-x-2'>
              {entity.enabled ? (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                  Enabled
                </span>
              ) : (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                  Disabled
                </span>
              )}
              {entity.forceUpdate && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700'>
                  Force Update
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }}
    renderDetails={entity => (
      <>
        <div>
          <dt className='text-xs text-grey-500'>Platform</dt>
          <dd className='text-sm text-grey-900 capitalize'>
            {entity.platform === 'ios' ? 'iOS' : 'Android'}
          </dd>
        </div>
        <div>
          <dt className='text-xs text-grey-500'>Minimum Version</dt>
          <dd>
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
              {entity.minimumVersion}
            </span>
          </dd>
        </div>
        <div>
          <dt className='text-xs text-grey-500'>Latest Version</dt>
          <dd>
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
              {entity.latestVersion}
            </span>
          </dd>
        </div>
      </>
    )}
    renderExtraSections={entity => (
      <>
        {entity.updateMessage && (
          <div className='bg-blue-50 rounded-lg p-4'>
            <h4 className='text-sm font-medium text-grey-700 mb-2'>
              Update Message
            </h4>
            <p className='text-sm text-grey-600'>{entity.updateMessage}</p>
          </div>
        )}
        {entity.appStoreUrl && (
          <div className='bg-grey-50 rounded-lg p-4'>
            <h4 className='text-sm font-medium text-grey-700 mb-2'>
              Store URL
            </h4>
            <a
              href={entity.appStoreUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm text-blue-600 hover:underline break-all'
            >
              {entity.appStoreUrl}
            </a>
          </div>
        )}
      </>
    )}
    renderForm={props => (
      <AppVersionForm
        appVersion={props.entity}
        onSubmit={props.onSubmit}
        onCancel={props.onCancel}
        loading={props.loading}
      />
    )}
    getId={entity => entity.id!}
    getCreatedAt={entity => entity.createdAt}
    getUpdatedAt={entity => entity.updatedAt}
  />
);
